import React, { useMemo, useState, useCallback } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';
import {
  Alert, Button, Input, Modal, Form, Space, Typography, message, Checkbox, InputNumber, Row, Col,
} from 'antd';
import {
  EnvironmentOutlined, EditOutlined, PlusOutlined, ReloadOutlined,
} from '@ant-design/icons';
import { transportApi, TransportStop, StopCreatePayload } from '../../api/transport';

L.Marker.prototype.options.icon = L.icon({
  iconUrl: icon,
  shadowUrl: iconShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
});

const { Text } = Typography;

const AKSAI_CENTER: [number, number] = [51.168, 53.02];
const AKSAI_ZOOM = 14;

interface StopsMapTabProps {
  stops: TransportStop[];
  onRefresh: () => void;
}

interface ClickHandlerProps {
  onMapClick: (lat: number, lng: number) => void;
}

const MapClickHandler: React.FC<ClickHandlerProps> = ({ onMapClick }) => {
  useMapEvents({
    click: (e) => {
      onMapClick(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
};

const isFakeCoord = (s: TransportStop): boolean =>
  s.latitude != null && s.longitude != null &&
  Math.abs(s.latitude - 51.16) < 0.001 && Math.abs(s.longitude - 53.0) < 0.001;

const StopsMapTab: React.FC<StopsMapTabProps> = ({ stops, onRefresh }) => {
  const [search, setSearch] = useState('');
  const [onlyFake, setOnlyFake] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [editingStop, setEditingStop] = useState<TransportStop | null>(null);
  const [newCoords, setNewCoords] = useState<{ lat: number; lng: number } | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [editForm] = Form.useForm();
  const [createForm] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();

  const stopsWithCoords = useMemo(
    () => stops.filter((s) => s.latitude != null && s.longitude != null),
    [stops],
  );
  const stopsWithoutCoords = stops.length - stopsWithCoords.length;

  const fakeCount = useMemo(() => stopsWithCoords.filter(isFakeCoord).length, [stopsWithCoords]);

  const filteredStops = useMemo(() => {
    let base = onlyFake ? stopsWithCoords.filter(isFakeCoord) : stopsWithCoords;
    const q = search.trim().toLowerCase();
    if (!q) return base;
    return base.filter((s) => {
      const ru = (s.name_ru || '').toLowerCase();
      const kk = (s.name_kk || '').toLowerCase();
      return ru.includes(q) || kk.includes(q) || String(s.id).includes(q);
    });
  }, [stopsWithCoords, search, onlyFake]);

  const handleDragEnd = useCallback(
    async (stop: TransportStop, lat: number, lng: number) => {
      try {
        await transportApi.updateStop(stop.id, {
          latitude: lat,
          longitude: lng,
          name_ru: stop.name_ru,
          name_kk: stop.name_kk,
        });
        messageApi.success(`Координаты обновлены: ${stop.name_ru || stop.id}`);
        onRefresh();
      } catch (err: any) {
        messageApi.error(err?.response?.data?.detail || 'Ошибка обновления');
        onRefresh();
      }
    },
    [messageApi, onRefresh],
  );

  const openEdit = (stop: TransportStop) => {
    setEditingStop(stop);
    editForm.setFieldsValue({
      name_ru: stop.name_ru,
      name_kk: stop.name_kk,
      latitude: stop.latitude,
      longitude: stop.longitude,
    });
    setEditOpen(true);
  };

  const handleEditSubmit = async () => {
    if (!editingStop) return;
    let values: { name_ru?: string; name_kk?: string; latitude?: number; longitude?: number };
    try {
      values = await editForm.validateFields();
    } catch {
      return;
    }
    setSubmitting(true);
    try {
      await transportApi.updateStop(editingStop.id, {
        name_ru: values.name_ru,
        name_kk: values.name_kk,
        latitude: values.latitude,
        longitude: values.longitude,
      });
      messageApi.success('Остановка обновлена');
      setEditOpen(false);
      onRefresh();
    } catch (err: any) {
      messageApi.error(err?.response?.data?.detail || 'Ошибка');
    } finally {
      setSubmitting(false);
    }
  };

  const handleMapClick = (lat: number, lng: number) => {
    setNewCoords({ lat, lng });
    createForm.resetFields();
    createForm.setFieldsValue({
      latitude: Number(lat.toFixed(6)),
      longitude: Number(lng.toFixed(6)),
    });
    setCreateOpen(true);
  };

  const handleCreateSubmit = async () => {
    if (!newCoords) return;
    let values: { name_ru?: string; name_kk?: string };
    try {
      values = await createForm.validateFields();
    } catch {
      return;
    }
    setSubmitting(true);
    try {
      const payload: StopCreatePayload = {
        name_ru: values.name_ru,
        name_kk: values.name_kk,
        latitude: newCoords.lat,
        longitude: newCoords.lng,
      };
      await transportApi.createStop(payload);
      messageApi.success('Остановка создана');
      setCreateOpen(false);
      setNewCoords(null);
      onRefresh();
    } catch (err: any) {
      messageApi.error(err?.response?.data?.detail || 'Ошибка');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ padding: 16 }}>
      {contextHolder}

      <Space style={{ marginBottom: 12, width: '100%', justifyContent: 'space-between' }} wrap>
        <Space wrap>
          <Input.Search
            allowClear
            placeholder="Поиск по названию или ID"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ width: 320 }}
          />
          <Checkbox checked={onlyFake} onChange={(e) => setOnlyFake(e.target.checked)}>
            Только с заглушкой координат ({fakeCount})
          </Checkbox>
          <Text type="secondary">
            На карте: {filteredStops.length} из {stopsWithCoords.length}
          </Text>
        </Space>
        <Button icon={<ReloadOutlined />} onClick={onRefresh}>
          Обновить
        </Button>
      </Space>

      {stopsWithoutCoords > 0 && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message={`${stopsWithoutCoords} остановок без координат — не отображаются на карте`}
        />
      )}

      <div style={{ height: 600, width: '100%', borderRadius: 8, overflow: 'hidden', border: '1px solid var(--l-line)' }}>
        <MapContainer
          center={AKSAI_CENTER}
          zoom={AKSAI_ZOOM}
          style={{ height: '100%', width: '100%' }}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a> contributors, &copy; CARTO'
            url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}.png"
          />
          <MapClickHandler onMapClick={handleMapClick} />
          {filteredStops.map((stop) => (
            <Marker
              key={stop.id}
              position={[stop.latitude as number, stop.longitude as number]}
              draggable
              eventHandlers={{
                dragend: (e) => {
                  const marker = e.target as L.Marker;
                  const pos = marker.getLatLng();
                  handleDragEnd(stop, pos.lat, pos.lng);
                },
              }}
            >
              <Popup>
                <div style={{ minWidth: 200 }}>
                  <div style={{ fontWeight: 600, marginBottom: 4 }}>
                    {stop.name_ru || 'Без названия'}
                  </div>
                  {stop.name_kk && (
                    <div style={{ color: 'var(--l-muted)', fontSize: 12, marginBottom: 4 }}>
                      {stop.name_kk}
                    </div>
                  )}
                  <div style={{ fontSize: 12, color: 'var(--l-text)', marginBottom: 4 }}>
                    ID: {stop.id}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--l-text)', marginBottom: 8 }}>
                    {stop.latitude?.toFixed(6)}, {stop.longitude?.toFixed(6)}
                  </div>
                  {typeof (stop as TransportStop & { route_count?: number }).route_count === 'number' && (
                    <div style={{ marginBottom: 8 }}>
                      {((stop as TransportStop & { route_count?: number }).route_count || 0) > 0 ? (
                        <span style={{ fontSize: 12 }}>Маршрутов: {(stop as TransportStop & { route_count?: number }).route_count}</span>
                      ) : (
                        <span style={{ fontSize: 12, color: 'var(--l-faint)' }}>не используется</span>
                      )}
                    </div>
                  )}
                  <Button
                    size="small"
                    type="primary"
                    icon={<EditOutlined />}
                    onClick={() => openEdit(stop)}
                    style={{ background: 'var(--l-accent)' }}
                  >
                    Редактировать
                  </Button>
                </div>
              </Popup>
            </Marker>
          ))}
        </MapContainer>
      </div>

      <Alert
        type="info"
        showIcon
        icon={<EnvironmentOutlined />}
        style={{ marginTop: 12 }}
        message="Перетащите маркер чтобы изменить координаты. Кликните по карте чтобы добавить новую остановку."
      />

      <Modal
        title="Редактировать остановку"
        open={editOpen}
        onCancel={() => setEditOpen(false)}
        onOk={handleEditSubmit}
        confirmLoading={submitting}
        okText="Сохранить"
        cancelText="Отмена"
        destroyOnClose
      >
        <Form form={editForm} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item name="name_ru" label="Название (рус.)">
            <Input placeholder="Центральный рынок" />
          </Form.Item>
          <Form.Item name="name_kk" label="Атауы (каз.)">
            <Input placeholder="Орталық базар" />
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="latitude" label="Широта" rules={[{ required: true, message: 'Укажите широту' }]}>
                <InputNumber style={{ width: '100%' }} step={0.000001} precision={6} placeholder="51.168000" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="longitude" label="Долгота" rules={[{ required: true, message: 'Укажите долготу' }]}>
                <InputNumber style={{ width: '100%' }} step={0.000001} precision={6} placeholder="53.020000" />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      <Modal
        title={
          <Space>
            <PlusOutlined />
            Создать остановку
          </Space>
        }
        open={createOpen}
        onCancel={() => {
          setCreateOpen(false);
          setNewCoords(null);
        }}
        onOk={handleCreateSubmit}
        confirmLoading={submitting}
        okText="Создать"
        cancelText="Отмена"
        okButtonProps={{ style: { background: 'var(--l-accent)' } }}
        destroyOnClose
      >
        <Form form={createForm} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item name="name_ru" label="Название (рус.)" rules={[{ required: true, message: 'Введите название' }]}>
            <Input placeholder="Центральный рынок" />
          </Form.Item>
          <Form.Item name="name_kk" label="Атауы (каз.)">
            <Input placeholder="Орталық базар" />
          </Form.Item>
          {newCoords && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              Координаты: {newCoords.lat.toFixed(6)}, {newCoords.lng.toFixed(6)}
            </Text>
          )}
        </Form>
      </Modal>
    </div>
  );
};

export default StopsMapTab;
